// The words the findings UI needs, as ONE flat shape the components take as a prop. DES2 writes these in
// 24 languages into the app-publish namespace; `findingsCopyFrom` maps the namespace's keys onto this shape in
// one place, so a renamed key is a one-file change. Components never build a sentence from a rule id, a scanner
// name or advisory text: only these strings plus the safe fields of the projection.
export type FindingsCopy = {
  summaryChecking: string;
  summaryClean: string;
  summaryWarnings: (count: number) => string;
  summaryHeldSecret: string;
  summaryHeldDependency: string;
  summaryHeldMany: (count: number) => string;
  summaryExpired: string;
  summaryIncomplete: string;
  summaryHeldPrivate: string;
  /** {time} */
  lastChecked: string;
  tagCritical: string;
  tagHigh: string;
  tagModerate: string;
  tagLow: string;
  tagUnrated: string;
  secretTitleApiKey: string;
  secretTitlePrivateKey: string;
  secretTitleAccessToken: string;
  secretTitlePassword: string;
  secretTitleUnknown: string;
  /** "Code: src/app.js, line 12"; the area word and the line are the namespace's, the file the projection's. */
  where: (area: 'code' | 'built', file: string, line: number | null) => string;
  secretWhy: string;
  secretDo: string;
  /** {package} {version} {severity} */
  depTitle: string;
  depTitleUnrated: string;
  depWhere: string;
  depWhy: string;
  depViewAdvisory: string;
  depDoUpdate: string;
  depDoNoFix: string;
  depDoUnrated: string;
  /** severity words for {severity}: critical / serious / moderate / minor */
  severityWordCritical: string;
  severityWordHigh: string;
  severityWordModerate: string;
  severityWordLow: string;
  actionFix: string;
  actionCheckAgain: string;
  actionReview: string;
  actionPublishAnyway: string;
  /** {package} {version} {severity} */
  anywayTitle: string;
  anywayBody: string;
  anywayConfirm: string;
  anywayCancel: string;
  /** {name} {date} */
  anywayDone: string;
  /** The request text a fix starts (the host sends ids to the server; this is only the pre-description). */
  fixRequestSecret: (label: string, file: string) => string;
  fixRequestPackage: (name: string) => string;
  chatHeldSecret: string;
  chatHeldMany: (count: number) => string;
  chatWarnings: (count: number) => string;
};

type NamespaceCopy = Readonly<Record<string, string>>;
const pick = (copy: NamespaceCopy, key: string) => copy[key] ?? '';

/**
 * Maps the app-publish namespace (DES2's keys) onto the flat shape the components take. Counts are the
 * namespace convention: a noun key in plural forms (formatCount prepends the number) filled into a
 * sentence key as {issues}/{things}.
 */
export function findingsCopyFrom(
  copy: NamespaceCopy,
  helpers: {
    formatCopy: (template: string, values: Record<string, string>) => string;
    formatCount: (forms: string, count: number, locale: string) => string;
    locale: string;
  },
): FindingsCopy {
  const { formatCopy, formatCount, locale } = helpers;
  const text = (key: string) => pick(copy, key);
  const issues = (n: number) => formatCount(text('checkIssues'), n, locale);
  const things = (n: number) => formatCount(text('checkThings'), n, locale);
  return {
    summaryChecking: text('checkChecking'),
    summaryClean: text('checkClean'),
    summaryWarnings: (n) => formatCopy(text('checkWarnings'), { things: things(n) }),
    summaryHeldSecret: text('checkHeldSecret'),
    summaryHeldDependency: text('checkHeldDependency'),
    summaryHeldMany: (n) => formatCopy(text('checkHeldMany'), { issues: issues(n) }),
    summaryExpired: text('checkExpired'),
    summaryIncomplete: text('checkIncomplete'),
    summaryHeldPrivate: text('checkHeldPrivate'),
    lastChecked: text('checkLastChecked'),
    tagCritical: text('severityCritical'),
    tagHigh: text('severityHigh'),
    tagModerate: text('severityModerate'),
    tagLow: text('severityLow'),
    tagUnrated: text('severityUnrated'),
    secretTitleApiKey: text('secretApiKey'),
    secretTitlePrivateKey: text('secretPrivateKey'),
    secretTitleAccessToken: text('secretAccessToken'),
    secretTitlePassword: text('secretPassword'),
    secretTitleUnknown: text('secretUnknown'),
    where: (area, file, line) =>
      line === null
        ? `${text(area === 'code' ? 'secretAreaCode' : 'secretAreaBuilt')} ${file}`
        : formatCopy(text('secretWhere'), {
            area: text(area === 'code' ? 'secretAreaCode' : 'secretAreaBuilt'),
            file,
            line: String(line),
          }),
    secretWhy: text('secretWhy'),
    secretDo: text('secretWhat'),
    depTitle: text('depTitle'),
    depTitleUnrated: text('depTitleUnrated'),
    depWhere: text('depWhere'),
    depWhy: text('depWhy'),
    depViewAdvisory: text('depViewAdvisory'),
    depDoUpdate: text('depAdviceUpdate'),
    depDoNoFix: text('depAdviceNoFix'),
    depDoUnrated: text('depAdviceUnrated'),
    severityWordCritical: text('severityWordCritical'),
    severityWordHigh: text('severityWordHigh'),
    severityWordModerate: text('severityWordModerate'),
    severityWordLow: text('severityWordLow'),
    actionFix: text('actionFixAndRecheck'),
    actionCheckAgain: text('actionCheckAgain'),
    actionReview: text('actionReview'),
    actionPublishAnyway: text('actionPublishAnyway'),
    anywayTitle: text('publishAnywayTitle'),
    anywayBody: text('publishAnywayBody'),
    anywayConfirm: text('publishAnywayConfirm'),
    anywayCancel: text('publishAnywayCancel'),
    anywayDone: text('publishAnywayAcknowledged'),
    fixRequestSecret: (label, file) => formatCopy(text('fixRequestSecret'), { label, file }),
    fixRequestPackage: (name) => formatCopy(text('fixRequestPackage'), { package: name }),
    chatHeldSecret: text('chatHeldSecret'),
    chatHeldMany: (n) => formatCopy(text('chatHeldMany'), { issues: issues(n) }),
    chatWarnings: (n) => formatCopy(text('chatWarnings'), { things: things(n) }),
  };
}
